<template>
  <!-- One alternate contact (ContactRecipient): same look and actions as ContactPanel's
       header card — details, Send Text, log a call/outside text/note, copy/send links —
       without the family-level info (students, address). Logs go on the family's contact
       with this alternate's recipient_id (the parent opens the note prompt). -->
  <div class="rounded-xl border border-contact-card-sub-border dark:border-contact-card-sub-border-dark bg-contact-card-sub-bg dark:bg-contact-card-sub-bg-dark px-3 py-2 space-y-2">
    <div class="flex items-start gap-2.5">
      <div
        class="h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold"
        :class="!recipient.notify && 'opacity-50'"
        :style="{ background: avatarGradient(recipient.name) }"
      >{{ initials(recipient.name) || '?' }}</div>
      <div class="flex-1 min-w-0">
        <!-- Line 1: name + one-word pills -->
        <div class="flex items-center gap-1.5 min-w-0">
          <h4 class="font-semibold text-sm text-fg-base dark:text-fg-base-dark truncate">{{ recipient.name }}</h4>
          <span v-if="recipient.label" :class="[PILL, 'bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark truncate']">{{ recipient.label }}</span>
          <span v-if="recipient.billing_access" :class="[PILL, 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300']" :title="FULL_ACCESS_HINT">Full</span>
          <span v-else :class="[PILL, 'bg-surface-muted dark:bg-surface-muted-dark text-fg-muted dark:text-fg-muted-dark']"
            title="Their account link is read-only: lessons and teachers, no payments">View</span>
          <span v-if="!recipient.notify" :class="[PILL, 'bg-surface-muted dark:bg-surface-muted-dark text-fg-faint dark:text-fg-faint-dark']"
            title="Gets no emails or texts">Muted</span>
        </div>
        <!-- Line 2: phone and email -->
        <div v-if="!editing" class="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm min-w-0">
          <span class="flex items-center gap-1.5">
            <PhoneIcon class="h-3.5 w-3.5 flex-shrink-0 text-fg-faint dark:text-fg-faint-dark" />
            <span :class="recipient.phone ? 'text-fg-secondary dark:text-fg-secondary-dark' : 'text-fg-faint dark:text-fg-faint-dark'">{{ recipient.phone || 'No phone' }}</span>
            <span v-if="recipient.phone && !recipient.sms_consent" class="text-xs text-fg-faint dark:text-fg-faint-dark" title="They haven't agreed to texts on their preferences page">· no texts</span>
          </span>
          <span class="flex items-center gap-1.5 min-w-0">
            <EnvelopeIcon class="h-3.5 w-3.5 flex-shrink-0 text-fg-faint dark:text-fg-faint-dark" />
            <RouterLink v-if="recipient.email && canEmail" :to="{ name: 'AdminEmailsInbox', query: { compose: recipient.email } }" title="Send an email"
              class="truncate text-fg-secondary dark:text-fg-secondary-dark hover:underline">{{ recipient.email }}</RouterLink>
            <a v-else-if="recipient.email" :href="`mailto:${recipient.email}`" class="truncate text-fg-secondary dark:text-fg-secondary-dark hover:underline">{{ recipient.email }}</a>
            <span v-else class="text-fg-faint dark:text-fg-faint-dark">No email</span>
          </span>
        </div>
      </div>
      <!-- Edit top-right; Send Text / log / links pinned bottom-right. The column stretches
           to the two text lines, so the card stays as short as they are. -->
      <div v-if="!editing" class="self-stretch flex flex-col justify-between items-end flex-shrink-0 -mb-1">
        <button @click="startEdit" class="text-xs leading-4 text-blue-600 dark:text-blue-400 hover:underline">Edit</button>
        <div class="flex items-center -mr-1">
          <RouterLink v-if="canText" :to="{ name: 'AdminSmsInbox', query: { phone: recipient.phone } }" title="Send a text" :class="iconBtn">
            <ChatBubbleLeftEllipsisIcon class="h-4 w-4" />
          </RouterLink>
          <LogTouchButtons @log="$emit('log', $event)" :button-class="iconBtn" />
          <ActionMenuButton :icon="LinkIcon" title="Links" down :button-class="iconBtn"
            :sections="linksMenu" :busy-key="busy" :done="done" />
        </div>
      </div>
    </div>

    <!-- Edit -->
    <div v-if="editing" class="space-y-2">
      <div v-for="f in FIELDS" :key="f.key">
        <label class="block text-xs font-medium text-fg-muted dark:text-fg-muted-dark mb-1">{{ f.label }}</label>
        <input v-model="draft[f.key]" :type="f.type" :placeholder="f.placeholder" autocomplete="off"
          @keydown.enter="save" @keydown.esc="editing = false"
          class="w-full px-2.5 py-1.5 rounded border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
      </div>
      <label class="flex items-center gap-2 text-sm text-fg-secondary dark:text-fg-secondary-dark">
        <input v-model="draft.notify" type="checkbox" class="rounded" /> Gets notifications
      </label>
      <label class="flex items-center gap-2 text-sm text-fg-secondary dark:text-fg-secondary-dark" :title="FULL_ACCESS_HINT">
        <input v-model="draft.billing_access" type="checkbox" class="rounded" /> Full account access
      </label>
      <div class="flex items-center gap-2">
        <button @click="save" :disabled="saving || !draft.name.trim()"
          class="px-3 py-1 rounded-md bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium">{{ saving ? 'Saving…' : 'Save' }}</button>
        <button @click="editing = false" class="px-3 py-1 rounded-md border border-stroke-strong dark:border-stroke-strong-dark text-xs text-fg-secondary dark:text-fg-secondary-dark">Cancel</button>
        <button @click="makePrimary" :disabled="saving" class="ml-auto text-xs text-blue-600 dark:text-blue-400 hover:underline">Make primary contact</button>
        <button @click="remove" class="text-xs text-red-600 dark:text-red-400 hover:underline">Remove</button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  BellIcon, ChatBubbleLeftEllipsisIcon, ChatBubbleLeftIcon, EnvelopeIcon, IdentificationIcon, LinkIcon, PhoneIcon,
} from '@heroicons/vue/24/outline'
import ActionMenuButton from '../common/ActionMenuButton.vue'
import LogTouchButtons from '../common/LogTouchButtons.vue'
import { avatarGradient, initials } from '../../composables/useAvatarGradient.js'
import { useApi } from '../../composables/useApi.js'

const props = defineProps({
  recipient: { type: Object, required: true },
  contactId: { type: Number, required: true },
  canEmail:  { type: Boolean, default: false },
  iconBtn:   { type: String, required: true },
})
const emit = defineEmits(['updated', 'deleted', 'log', 'primary-changed'])
const { fetchApi } = useApi()
const route = useRoute()

const url = (path = '') => `/api/contacts/${props.contactId}/recipients/${props.recipient.id}${path}`
const canText = computed(() => !!(props.recipient.phone && props.recipient.sms_consent) && route.name !== 'AdminSmsInbox')

// ── Edit ────────────────────────────────────────────────────────────────────
const FIELDS = [
  { key: 'name',  label: 'Name',  type: 'text' },
  { key: 'label', label: 'Label', type: 'text', placeholder: 'Dad, Grandma, Sitter…' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'tel' },
]
const editing = ref(false)
const saving  = ref(false)
const draft   = ref({})

function startEdit() {
  const r = props.recipient
  draft.value = { name: r.name, label: r.label || '', email: r.email || '', phone: r.phone || '',
                  notify: !!r.notify, billing_access: !!r.billing_access }
  editing.value = true
}

async function save() {
  if (!draft.value.name.trim()) return
  saving.value = true
  try {
    const d = draft.value
    emit('updated', await fetchApi(url(), {
      method: 'PATCH',
      body: JSON.stringify({
        name: d.name.trim(), label: d.label.trim() || null, email: d.email.trim() || null,
        phone: d.phone.trim() || null, notify: d.notify, billing_access: d.billing_access,
      }),
    }))
    editing.value = false
  } catch (e) {
    alert(e.message || 'Failed to save.')
  } finally {
    saving.value = false
  }
}

const PILL = 'text-xs px-1.5 py-0.5 rounded flex-shrink-0'
const FULL_ACCESS_HINT = 'Payment emails and texts, and their account link can do everything the primary’s can: update the card, pay, buy packages, accept invites, request lessons'

// Swap who's primary (alternates.make_primary): the family's account stays, the people trade places.
async function makePrimary() {
  const r = props.recipient
  if (!confirm(`Make ${r.name} the primary contact?

` +
    `• ${r.name} takes over the account: emails, texts, collections notices, and the Stripe customer name.
` +
    `• The current primary becomes an alternate contact with full account access.
` +
    `• Each person keeps their own notification settings.
` +
    `• Every account link already sent stops working — send new ones afterwards.`)) return
  saving.value = true
  try {
    const { stripe_warning } = await fetchApi(url('/make-primary'), { method: 'POST' })
    if (stripe_warning) alert(stripe_warning)
    editing.value = false
    emit('primary-changed')
  } catch (e) {
    alert(e.message || 'Failed to make primary.')
  } finally {
    saving.value = false
  }
}

async function remove() {
  if (!confirm(`Remove ${props.recipient.name}? Their account link stops working.`)) return
  await fetchApi(url(), { method: 'DELETE' })
  emit('deleted', props.recipient.id)
}

// ── Links: their own read-only account page, and their own preferences ──────
const busy = ref(null)
const done = ref(false)

async function run(key, fn) {
  busy.value = key
  try {
    await fn()
    done.value = true
    setTimeout(() => { done.value = false }, 1500)
  } catch (e) {
    alert(e.message || 'Something went wrong.')
  } finally {
    busy.value = null
  }
}

const accountLink = (channel) => fetchApi(url('/account-link'), { method: 'POST', body: JSON.stringify({ channel }) })

const linksMenu = computed(() => {
  const r = props.recipient
  const item = (key, label, icon, fn, extra = {}) =>
    ({ key, label, icon, loadingLabel: key.startsWith('copy') ? 'Generating…' : 'Sending…', onClick: () => run(key, fn), ...extra })
  return [
    { items: [
      item('copy-account', 'Copy account page link', IdentificationIcon,
        async () => navigator.clipboard.writeText((await accountLink('copy')).link_url),
        { title: 'The family’s page, read-only — payments only if they can see billing' }),
    ] },
    { header: 'Send', items: [
      item('email-account', 'Email account page link', EnvelopeIcon, () => accountLink('email'), { visible: !!r.email }),
      item('text-account', 'Text account page link', ChatBubbleLeftIcon, () => accountLink('sms'), { visible: !!(r.phone && r.sms_consent) }),
      // Rotates their preferences token, so an earlier preferences link stops working.
      item('email-prefs', 'Email notification preferences', BellIcon,
        () => fetchApi(url('/send-preferences-link'), { method: 'POST', body: JSON.stringify({ mode: 'full' }) }),
        { visible: !!r.email }),
    ] },
  ]
})
</script>
